import {
  all,
  call,
  fork,
  put,
  race,
  take,
  takeLatest,
} from 'typed-redux-saga/macro';

import {
  CLOSE_DOCUMENT,
  OPEN_DOCUMENT_SUCCESS,
  PAGE_SEARCHABILITY_SCAN_DONE,
  PDF_TEXT_PREPASS_DONE,
  SET_PAGE_SEARCHABILITY,
  SET_PAGE_TEXT_BATCH,
  SET_SEARCHABILITY_SCAN_PROGRESS,
  SET_TEXT_INDEX_PROGRESS,
} from '../actions';
import {
  extractPdfPagePrepass,
  PagePrepassEntry,
  PrepassChunk,
  PrepassEnd,
} from '../pdfPagePrepass';
import { classifyPdfPage } from '@redact-components/DocumentEditorPage/mupdfRenderWorkerClient';
import {
  recordMilestone,
  recordSpan,
} from '@redact-components/DocumentEditorPage/mainThreadSpans';

/**
 * Extract every page's text / geometry / dimensions once via MuPDF (no
 * rendering) and stream them into redux as SET_PAGE_TEXT_BATCH chunks (VE-25293,
 * VE-26267). This is what lets the editor canvas virtualize pages while keyword
 * search, occurrence counts, and the searchability warnings still cover the
 * whole document.
 *
 * The pre-pass leaves text-less pages provisionally `blank` (it never decodes a
 * page just to classify it — that blocked scanned docs for tens of seconds,
 * VE-27250). Once the batch is dispatched we FORK a background pass that runs a
 * cheap MuPDF raster probe over the text-less pages and promotes the ones that
 * actually paint content to `unsearchable`, populating the document-level
 * "N of K pages have no searchable text" count without blocking first paint.
 * (A text-less page that scrolls into view additionally refines itself with the
 * exact getOperatorList scan in DocumentPage.)
 *
 * Best-effort: a failed extraction does not throw — the render-path (for the
 * pages that do render) still backfills the same maps, so this can only add
 * coverage, never remove it. It IS reported, though (see below).
 *
 * On completion OR failure (not on cancellation) it dispatches
 * PDF_TEXT_PREPASS_DONE (`{ failed: true }` on failure), which flips
 * `session.textIndexReady` — the search rail shows an "indexing…" spinner until
 * then, since keyword results only cover render-path pages while this runs.
 *
 * A stream the extractor GAVE UP on is a failure too, though nothing throws:
 * it swallows a range that keeps failing after retries, stops, and says so in
 * its return value. Without that, a document whose index died at page 300 of
 * 1,602 (or at page 1) read as fully indexed, and a lexicon applied to it as a
 * clean apply (VE-29686). A stream that ends short of its own `total` without
 * saying so is treated the same way, as a backstop.
 */
export function* runPdfPrepass(pdfUrl: string) {
  if (!pdfUrl) {
    // Still signal DONE. Returning before the pre-pass ran left
    // `textIndexReady` false for the session, and since the rail's indexing box
    // gates on that alone, a document whose asset URI is missing showed "No PDF
    // loaded for this document." in the canvas next to a spinner reading
    // "Indexing document…" for ever. `openDocument` sets `pdfUrl: mediaUri ??
    // ''`, so this is reachable.
    yield* put(PDF_TEXT_PREPASS_DONE());
    // Same reasoning for the scan: nothing will ever probe a document with no
    // url, so anything gated on the scan finishing has to be let go here too.
    yield* put(PAGE_SEARCHABILITY_SCAN_DONE());
    return;
  }
  let failed = false;
  try {
    const handle = yield* call(openPrepassIterator, pdfUrl);
    // Accumulate every chunk so the searchability post-pass sees all pages.
    const allPages: PagePrepassEntry[] = [];
    let last: { indexed: number; total: number } | null = null;
    for (;;) {
      const res = yield* call(pullPrepassChunk, handle);
      if (res.done) {
        // Anything but an explicit "did not fail" is a failure. Fail closed,
        // without throwing: a throw here would skip the searchability fork for
        // the pages that did arrive.
        if (res.value?.failed !== false) {
          failed = true;
        }
        break;
      }
      const { chunk, indexed, total } = res.value;
      last = { indexed, total };
      allPages.push(...chunk);
      // Stream each chunk into redux: the search saga re-runs on
      // SET_PAGE_TEXT_BATCH, so results grow as pages index instead of appearing
      // all at once minutes later; the progress drives the rail's spinner.
      // Covers the reducer, immer (which DEEP-FREEZES every glyph object in a
      // development build) and the subscriber notification it triggers.
      recordMilestone('first text chunk indexed');
      const dispatchedAt = performance.now();
      yield* put(SET_PAGE_TEXT_BATCH({ pages: chunk }));
      recordSpan(
        'prepass: SET_PAGE_TEXT_BATCH dispatch',
        performance.now() - dispatchedAt
      );
      yield* put(SET_TEXT_INDEX_PROGRESS({ indexed, total }));
    }
    // Stopped short of its own count (see the header). Either way the pages
    // that did arrive stand and still get scanned.
    if (last !== null && last.indexed < last.total) {
      failed = true;
    }
    if (allPages.length > 0) {
      // Fill in scan-vs-blank for the text-less pages once all are indexed — off
      // the first-paint critical path. The fork owns signalling its own
      // completion, including the nothing-to-probe case.
      yield* fork(classifyTextlessSearchability, pdfUrl, allPages);
    } else {
      // No pages at all, so nothing will ever be probed. Say so, or anything
      // waiting on the scan waits for ever.
      yield* put(PAGE_SEARCHABILITY_SCAN_DONE());
    }
  } catch (err) {
    // Non-fatal: keyword search etc. degrade to render-path backfill.
    console.error('[pdfPrepass] page pre-pass failed:', pdfUrl, err);
    failed = true;
    // The fork never happened, so nothing else will ever report the scan
    // finished. A gate waiting on it must not hang on a failed pre-pass.
    yield* put(PAGE_SEARCHABILITY_SCAN_DONE());
  }
  // Indexed, or given up: the rail stops showing "indexing…". Not in a
  // `finally`: once cancelled by a switch, this would land on the NEXT
  // document and mark its index ready before it had text (VE-29502).
  yield* put(PDF_TEXT_PREPASS_DONE(failed ? { failed: true } : undefined));
}

/** Opaque handle wrapping the streaming iterator (see openPrepassIterator). */
export interface PrepassHandle {
  iterator: AsyncGenerator<PrepassChunk, PrepassEnd, unknown>;
}

// Split out so the saga can `call` them (and tests can match the effects).
// CRITICAL: `extractPdfPagePrepass` is an async generator, so its iterator has
// `.next`/`.throw` — which redux-saga's `call` would mistake for a child saga
// and try to RUN, breaking the pre-pass. Wrap it in a plain object so `call`
// hands the handle back as a value; `pullPrepassChunk` returns a Promise (which
// `call` awaits correctly), never a bare iterator.
export const openPrepassIterator = (pdfUrl: string): PrepassHandle => ({
  iterator: extractPdfPagePrepass(pdfUrl),
});
export const pullPrepassChunk = (handle: PrepassHandle) =>
  handle.iterator.next();

/**
 * How many searchability probes may be outstanding at once.
 *
 * This USED to be unbounded (`all()` over every text-less page), which on a
 * fully-scanned long document meant one queued MuPDF job PER PAGE — ~1600 of
 * them on our 1600-page test file — all at CLASSIFY priority, i.e. ahead of the
 * thumbnail rail's background sweep. Each probe is individually tiny (a 40px
 * raster), so the queue drained fine; the problem was that it sat in front of
 * every off-screen thumbnail for the whole of that drain. Bounded, the count
 * still fills in promptly (the probes are cheap and the queue interleaves) while
 * the rail keeps its share of the worker.
 */
const PROBE_CONCURRENCY = 8;

/**
 * Background pass (forked, non-blocking): for every text-less page, run the
 * cheap MuPDF raster probe (`classifyPdfPage`) and promote it to `unsearchable`
 * when the page actually paints content. Pages that stay blank keep the
 * pre-pass's provisional `blank`, so a genuinely empty page never flashes the
 * "no searchable text" warning. Runs `PROBE_CONCURRENCY` probes at a time so the
 * count fills in promptly without burying the thumbnail sweep; a takeLatest
 * re-open cancels this fork with its parent.
 */
export function* classifyTextlessSearchability(
  pdfUrl: string,
  pages: PagePrepassEntry[]
) {
  const textLess = pages.filter((p) => p.searchability === 'blank');
  if (textLess.length === 0) {
    // Every page carries text, so there is nothing to tell apart. The scan is
    // finished the moment it is asked for.
    yield* put(PAGE_SEARCHABILITY_SCAN_DONE());
    return;
  }
  yield* put(
    SET_SEARCHABILITY_SCAN_PROGRESS({ scanned: 0, total: textLess.length })
  );
  // Counted across runners, not per runner: eight of them share the work list,
  // so only the total completed is meaningful.
  let scanned = 0;
  // Shared cursor over the work list. Sagas are cooperatively scheduled on one
  // thread, so a plain counter is safe here — no two runners can read the same
  // index. Each runner pulls the next page until the list is exhausted.
  let next = 0;
  const runnerCount = Math.min(PROBE_CONCURRENCY, textLess.length);
  yield* all(
    Array.from({ length: runnerCount }, () =>
      call(function* probeRunner() {
        for (;;) {
          const p = textLess[next];
          if (!p) {
            return;
          }
          next += 1;
          try {
            const { hasContent } = yield* call(
              classifyPdfPage,
              pdfUrl,
              p.page - 1
            );
            if (hasContent) {
              yield* put(
                SET_PAGE_SEARCHABILITY({ page: p.page, state: 'unsearchable' })
              );
            }
            // No content → leave the provisional `blank` (already in redux).
          } catch {
            // Probe failed (worker crash, etc.) — leave `blank`; the on-view
            // getOperatorList scan will still classify the page if it's visited.
          }
          // Counted whether the probe answered or threw: this drives a progress
          // row, and a page that failed is still a page no longer pending.
          scanned += 1;
          yield* put(
            SET_SEARCHABILITY_SCAN_PROGRESS({
              scanned,
              total: textLess.length,
            })
          );
        }
      })
    )
  );
  // Every runner has drained the list. Said HERE rather than by the caller,
  // which forked this and moved on — and deliberately after `all()`, so it
  // cannot be reported finished while probes are still outstanding.
  //
  // A cancelled fork (re-open, CLOSE_DOCUMENT) never reaches this, which is
  // correct: that session is gone, and the next open resets the flag anyway.
  yield* put(PAGE_SEARCHABILITY_SCAN_DONE());
}

/**
 * takeLatest so re-opening a document cancels an in-flight pre-pass (and its
 * forked searchability pass) for the previous one — and RACED against
 * CLOSE_DOCUMENT so leaving the editor does too.
 *
 * Without that race, closing a document left the whole pre-pass running: on a
 * 1602-page file that is ~65 more MuPDF extract jobs and their redux dispatches,
 * indexing a document nobody is looking at into a session that has been torn
 * down. It also kept the shared worker alive (every enqueue cancels its pending
 * release), so the ~10 MB WASM heap and the open document stayed resident too.
 * The visible symptom was console activity continuing for a while after
 * navigating back to the folder.
 */
export function* pdfPrepassSaga() {
  yield* takeLatest(OPEN_DOCUMENT_SUCCESS, function* ({ payload }) {
    // race cancels the loser, so CLOSE_DOCUMENT tears down runPdfPrepass and the
    // searchability pass it forked.
    yield* race({
      run: call(runPdfPrepass, payload.pdfUrl),
      closed: take(CLOSE_DOCUMENT),
    });
  });
}
